import { useMemo } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useCommunityMtoQuantities } from '../../api/mtos';
import { DataTable } from '../ui/DataTable';
import { Modal } from '../ui/Modal';
import page from '../../pages/Page.module.css';

type QtyRow = {
  item: string;
  payable_task_group: string;
  phase_name: string;
  sub_phase_name: string;
  quantities: Record<string, number | null>;
};

export function MtoQuantitiesModal({
  open,
  onClose,
  communityId,
}: {
  open: boolean;
  onClose: () => void;
  communityId: string | undefined;
}) {
  const { data, isLoading, isError, error } = useCommunityMtoQuantities(communityId, open);

  const mtos = data?.mtos ?? [];
  const rows = data?.rows ?? [];

  const columns = useMemo<ColumnDef<QtyRow>[]>(() => {
    const fixed: ColumnDef<QtyRow>[] = [
      {
        header: 'Line Item',
        accessorKey: 'item',
        meta: { minWidth: '160px', wrap: true },
      },
      {
        header: 'Payable Task Group',
        accessorKey: 'payable_task_group',
        meta: { minWidth: '140px', wrap: true },
      },
      {
        header: 'Phase',
        accessorKey: 'phase_name',
        meta: { minWidth: '100px', nowrap: true },
      },
      {
        header: 'Sub Phase',
        accessorKey: 'sub_phase_name',
        meta: { minWidth: '100px', nowrap: true },
      },
    ];
    const mtoCols: ColumnDef<QtyRow>[] = mtos.map((m) => ({
      id: `mto-${m.id}`,
      header: m.name,
      meta: { align: 'right' as const, minWidth: '88px', nowrap: true },
      cell: ({ row }) => {
        const key = String(m.id);
        if (!(key in row.original.quantities)) {
          return (
            <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>Missing</span>
          );
        }
        const qty = row.original.quantities[key];
        return <span>{qty != null ? String(qty) : '—'}</span>;
      },
    }));
    return [...fixed, ...mtoCols];
  }, [mtos]);

  const searchKeys = useMemo(
    () =>
      [
        'item',
        'payable_task_group',
        'phase_name',
        'sub_phase_name',
        (r: QtyRow) =>
          mtos
            .map((m) => {
              const key = String(m.id);
              if (!(key in r.quantities)) return 'Missing';
              const qty = r.quantities[key];
              return qty != null ? String(qty) : '';
            })
            .join(' '),
      ] as const,
    [mtos]
  );

  return (
    <Modal title="MTO Quantities" open={open} onClose={onClose} size="full">
      <p className={page.muted} style={{ marginTop: 0 }}>
        Quantities by line item across community MTOs. Cells marked{' '}
        <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>Missing</span> mean the
        item is not on that MTO.
      </p>
      {isLoading ? <p className={page.muted}>Loading quantities…</p> : null}
      {isError ? (
        <p style={{ color: 'var(--color-danger)' }}>
          {error instanceof Error ? error.message : 'Failed to load quantities'}
        </p>
      ) : null}
      {!isLoading && !isError && !mtos.length ? (
        <p className={page.muted}>No MTOs in this community.</p>
      ) : null}
      {!isLoading && !isError && mtos.length ? (
        <DataTable
          data={rows}
          columns={columns}
          layout="content"
          searchKeys={searchKeys}
          searchPlaceholder="Item, task group, phase…"
        />
      ) : null}
    </Modal>
  );
}
